import { useState } from "react";
import { useCategoriesQuery, useCreateCategory, useUpdateCategory, useDeleteCategory } from "../../hooks/category";
import { getExpenses } from "../../api/expense";
import { getIncomes } from "../../api/income";
import { 
  Box, 
  Typography, 
  Container, 
  useTheme,
  Fade,
  Grid,
  Avatar,
  Skeleton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { 
  Category as CategoryIcon, 
  LoginOutlined, 
  MoneyOff,
  AttachMoney,
  TrendingUp} from "@mui/icons-material";
import CategoryListComponent from "../../components/category/CategoryListComponent";
import Category from "../../models/finance/Category";
import { CategoryType } from "../../models/finance/CategoryType";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { PageContainer, ModernCard, GradientButton } from "../../components/common/StyledComponents";
import { useError } from "../../error/useError";
import ErrorAlert from "../../components/common/ErrorAlert";

const CategoryPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();

  const { error, helpText, clearError, handleError } = useError();
  const [addExpenseError, setAddExpenseError] = useState<string | null>(null);
  const [addIncomeError, setAddIncomeError] = useState<string | null>(null);

  const { data: expenseCategories = [], isLoading: loadingExpense, refetch: refetchExpense } = useCategoriesQuery(CategoryType.Expense, true);
  const { data: incomeCategories = [], isLoading: loadingIncome, refetch: refetchIncome } = useCategoriesQuery(CategoryType.Income, true);
  const { mutate: createCategory } = useCreateCategory();
  const { mutate: updateCategory } = useUpdateCategory();
  const { mutate: deleteCategory } = useDeleteCategory();

  const loading = loadingExpense || loadingIncome;
  
  const fetchAllCategories = () => {
    refetchExpense();
    refetchIncome();
  };

  const handleAddCategory = (title: string, type: CategoryType) => {
    createCategory(
      { title, type },
      {
        onSuccess: () => {
          // Clear inline errors on success
          if (type === CategoryType.Expense) setAddExpenseError(null); else setAddIncomeError(null);
        },
        onError: (error) => {
          console.error("Error adding category:", error);
          // Prefer inline field error for duplicate title or validation issues
          const message = error && (error as any).userMessage ? (error as any).userMessage : "Failed to add category";
          if (type === CategoryType.Expense) setAddExpenseError(message); else setAddIncomeError(message);
        }
      }
    );
  };

  const handleDeleteCategory = (
    category: Category,
    fallbackCategoryId?: string
  ) => {
    deleteCategory(
      { category, fallbackCategoryId },
      {
        onError: (error) => {
          console.error("Error deleting category:", error);
          handleError(error, "Failed to delete category");
        }
      }
    );
  };

  const handleUpdateCategory = (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => {
    updateCategory(
      { category: oldCategory, newTitle, newIsActive },
      {
        onError: (error) => {
          console.error("Error updating category:", error);
          handleError(error, "Failed to update category");
        }
      }
    );
  };

  const getFinanceItemsNumber = async (
    categoryType: CategoryType,
    categoryId: string
  ): Promise<number> => {
    try {
      if (categoryType === CategoryType.Expense) {
        const expenses = await getExpenses(categoryId);
        return expenses.length;
      } else {
        const incomes = await getIncomes(categoryId);
        return incomes.length;
      }
    } catch (error) {
      console.error("Error fetching finance items count:", error);
      return 0;
    }
  };

  // Guest view for non-logged-in users
  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Fade in={true} timeout={1000}>
            <Box>
              {/* Hero Section */}
              <Box 
                sx={{ 
                  textAlign: "center", 
                  mb: 8,
                  background: `linear-gradient(135deg, ${theme.palette.info.main}10 0%, ${theme.palette.info.dark}10 100%)`,
                  borderRadius: 4,
                  p: 6,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 30% 40%, rgba(33, 150, 243, 0.1) 0%, transparent 50%)'}
                }}
              >
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 3,
                      background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`}}
                  >
                    <CategoryIcon sx={{ fontSize: '2.5rem', color: 'white' }} />
                  </Avatar>
                  
                  <Typography
                    variant="h1"
                    gutterBottom
                    sx={{ 
                      mb: 3,
                      background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'}}
                  >
                    Organize with Categories
                  </Typography>

                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ mb: 4, maxWidth: 600, mx: "auto", fontWeight: 400 }}
                  >
                    Create custom categories to organize your expenses and income. 
                    Better categorization leads to better financial insights and control.
                  </Typography>

                  <GradientButton
                    variant="contained"
                    size="large"
                    startIcon={<LoginOutlined />}
                    onClick={() => navigate(LOGIN_ROUTE_PATH)}
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 25px ${theme.palette.info.main}40`},
                      transition: 'all 0.2s ease-in-out'}}
                  >
                    Sign In to Start Organizing
                  </GradientButton>
                </Box>
              </Box>

              {/* Features Grid */}
              <Grid container spacing={4} sx={{ mb: 6 }}>
                <Grid item xs={12} md={4}>
                  <ModernCard sx={{ height: '100%', textAlign: 'center', p: 4 }}>
                    <MoneyOff sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Expense Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create custom categories for different types of expenses to track where your money goes.
                    </Typography>
                  </ModernCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <ModernCard sx={{ height: '100%', textAlign: 'center', p: 4 }}>
                    <AttachMoney sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Income Categories
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Organize different income sources to understand your revenue streams better.
                    </Typography>
                  </ModernCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <ModernCard sx={{ height: '100%', textAlign: 'center', p: 4 }}>
                    <TrendingUp sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Better Insights
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Generate detailed reports and analytics based on your custom categories.
                    </Typography>
                  </ModernCard>
                </Grid>
              </Grid>

              {/* Benefits Card */}
              <ModernCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Why Use Categories?
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      📊 Track Patterns
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      See spending patterns by category over time
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      🎯 Set Budgets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create category-specific budget limits
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      📈 Generate Reports
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Get detailed insights and analytics
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      💡 Make Decisions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Make informed financial decisions
                    </Typography>
                  </Grid>
                </Grid>
              </ModernCard>
            </Box>
          </Fade>
        </Container>
      </PageContainer>
    );
  }

  // Loading state for logged-in users
  if (loading) {
    return (
      <PageContainer>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Fade in={true} timeout={600}>
            <Box>
              {/* Header Skeleton */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 6 }}>
                <Skeleton variant="circular" width={60} height={60} />
                <Box>
                  <Skeleton variant="text" width={300} height={40} />
                  <Skeleton variant="text" width={500} height={24} />
                </Box>
              </Box>

              {/* Category Lists Skeleton */}
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Container>
      </PageContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <ErrorAlert
            error={error}
            helpText={helpText}
            onClear={clearError}
            onRetry={fetchAllCategories}
            title="Category Error"
          />
        </Container>
      </PageContainer>
    );
  }

  // Logged-in user view
  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <CategoryIcon sx={{ fontSize: '2.5rem', color: theme.palette.info.main }} />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Category Management
                </Typography>
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Organize your expenses and income with custom categories
              </Typography>
            </Box>

            {/* Category Management Grid */}
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <CategoryListComponent
                  categoryType={CategoryType.Expense}
                  categories={expenseCategories}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onUpdateCategory={handleUpdateCategory}
                  getFinanceItemsNumber={getFinanceItemsNumber}
                  addError={addExpenseError}
                  onClearAddError={() => setAddExpenseError(null)}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <CategoryListComponent
                  categoryType={CategoryType.Income}
                  categories={incomeCategories}
                  onAddCategory={handleAddCategory}
                  onDeleteCategory={handleDeleteCategory}
                  onUpdateCategory={handleUpdateCategory}
                  getFinanceItemsNumber={getFinanceItemsNumber}
                  addError={addIncomeError}
                  onClearAddError={() => setAddIncomeError(null)}
                />
              </Grid>
            </Grid>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default CategoryPage;
